import {Box, FormControl, Stack, Textarea} from '@primer/react-brand'

import {isFormFieldTextArea} from '../../../schemas/contentful/contentTypes/formFieldTextArea'
import {isFormFieldTextInput} from '../../../schemas/contentful/contentTypes/formFieldTextInput'
import {isFormFieldSelect} from '../../../schemas/contentful/contentTypes/formFieldSelect'
import {isFormFieldCheckboxGroup} from '../../../schemas/contentful/contentTypes/formFieldCheckboxGroup'

import type {UseFormReturn} from '../../forms/Form/hooks/useForm'

import {ContentfulTextInput} from './formFields/ContentfulTextInput'
import {ContentfulSelectInput} from './formFields/ContentfulSelectInput'
import {ContentfulCheckboxGroupInput} from './formFields/ContentfulCheckboxGroupInput'

import {getValidation} from './validations'
import {getAnalyticsEvent} from '../../../lib/utils/analytics'

export type ContentfulFormTwoColumnLayoutProps = {
  rows: ReadonlyArray<{sys: {id: string}; fields: Record<string, unknown>}>
  formContext: UseFormReturn
}

export function ContentfulFormTwoColumnLayout({rows, formContext}: ContentfulFormTwoColumnLayoutProps) {
  const renderField = (formField: unknown) => {
    if (isFormFieldTextInput(formField)) {
      return <ContentfulTextInput key={formField.sys.id} component={formField} />
    }

    if (isFormFieldCheckboxGroup(formField)) {
      return <ContentfulCheckboxGroupInput key={formField.sys.id} component={formField} />
    }

    if (isFormFieldTextArea(formField)) {
      const required = formField.fields.validations?.some(validation => validation.fields.name === 'REQUIRED') ?? false

      const error = formContext.errors[formField.fields.htmlName]

      const validationErrorId = `${formField.fields.htmlName}-validation-msg`

      const validations = [
        // additional validations from Contentful
        ...(formField.fields.validations?.map(getValidation) ?? []),
      ]

      const {id, ...registerProps} = formContext.register(formField.fields.htmlName, {
        label: formField.fields.label,
        required,
        validations,
      })

      return (
        <FormControl
          key={formField.fields.htmlName}
          id={id}
          fullWidth
          required={required}
          validationStatus={typeof error === 'string' ? 'error' : undefined}
        >
          <FormControl.Label>{formField.fields.label}</FormControl.Label>

          <Textarea
            {...registerProps}
            {...getAnalyticsEvent({
              action: formField.fields.label,
              tag: 'input',
              context: 'form_control',
              location: 'form',
            })}
            aria-describedby={validationErrorId}
            placeholder={formField.fields.placeholder}
          />

          {typeof error === 'string' ? (
            <FormControl.Validation id={validationErrorId}>{error}</FormControl.Validation>
          ) : null}
        </FormControl>
      )
    }

    if (isFormFieldSelect(formField)) {
      return <ContentfulSelectInput key={formField.sys.id} component={formField} />
    }

    return null
  }

  return (
    <>
      {rows.map(row => {
        const rawFields = row.fields['formFields'] ?? row.fields['fields'] ?? []
        const fields = (rawFields as readonly unknown[]) ?? []

        // This case handles exactly 2 fields in a two-column layout, rendering two equal columns
        if (fields.length === 2) {
          return (
            <Stack
              key={row.sys.id}
              direction="horizontal"
              alignItems="flex-start"
              padding="none"
              style={{width: '100%'}}
            >
              <Box style={{flex: 5, minWidth: 0}}>{renderField(fields[0])}</Box>
              <Box style={{flex: 5, minWidth: 0}}>{renderField(fields[1])}</Box>
            </Stack>
          )
        }

        // This case handles 0 or 1 fields in a full width column
        return (
          <Stack key={row.sys.id} direction="horizontal" alignItems="flex-start" padding="none" style={{width: '100%'}}>
            <Box style={{flex: 10, minWidth: 0}}>{fields.map(f => renderField(f))}</Box>
          </Stack>
        )
      })}
    </>
  )
}

try{ ContentfulFormTwoColumnLayout.displayName ||= 'ContentfulFormTwoColumnLayout' } catch {}